'use client';

import { getProductsBySkusV2 } from '@/services/product/related-product-info';
import useWishlist from '@/store/wishlist.store';
import { useLocale } from 'next-intl';
import { useEffect, useRef, useState, useCallback } from 'react';
import useSWRMutation from 'swr/mutation';

const useWishlists = () => {
  const locale = useLocale();
  const spinnerRef = useRef(null);
  const pageRef = useRef(0);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [wishlistProducts, setWishlistProducts] = useState([]);
  const wishlist_skus = useWishlist((state) => state.wishlist);

  const { trigger } = useSWRMutation(
    `/whislist-products/${locale}`,
    async () =>
      await getProductsBySkusV2({
        locale,
        skuBulk: wishlist_skus,
        page: pageRef.current,
      }),
    {
      revalidateOnMount: false,
      revalidateOnFocus: false,
      onSuccess: (data) => {
        if (data?.data?.data?.products?.length) {
          setWishlistProducts((prev) => [...prev, ...data.data.data.products]);
          setHasMore(data.data.data.pages >= data.data.data.currentPage);
        } else {
          setHasMore(false);
        }
        setLoading(false);
      },
    }
  );

  const fetchMore = useCallback(() => {
    if (!hasMore || !wishlist_skus?.length) return;
    pageRef.current += 1;
    trigger();
  }, [hasMore, wishlist_skus, trigger]);

  useEffect(() => {
    if (wishlist_skus.length === 0) {
      setLoading(false);
    } else {
      trigger();
    }
  }, [wishlist_skus, trigger]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) fetchMore();
      },
      { threshold: 1 }
    );

    if (spinnerRef.current) observer.observe(spinnerRef.current);
    return () => observer.disconnect();
  }, [fetchMore]);

  return {
    wishlistProducts,
    wishlist_skus,
    hasMore,
    spinnerRef,
    loading,
  };
};

export default useWishlists;
